import { DateTime } from 'luxon';
import { crashReporterInterface, getLogger, tracker } from '../../utils/core';
import store from '../../utils/redux-store';

import * as actions from './bumpActions';
import * as bumpInterface from './bumpInterface';
import * as selectors from './bumpSelectors';
import * as bumpTypes from './bumpTypes';

const log = getLogger('flux/bump/bumpInstance');

let pingPongTimer = null;

let socket = null;
let socketBeingReconnected = null;

export const close = (code, context) => {
  log.debug(`Bump WebSocket: Close requested [${code}] '${context}'`, new Date().toLocaleString());
  if (socket?.readyState === WebSocket.CONNECTING || socket?.readyState === WebSocket.OPEN) {
    log.debug(`Bump WebSocket: Close triggered [${code}] '${context}'`, new Date().toLocaleString());
    socket.close(code, context);
  }
};

export const open = (context = 'unknown') => {
  log.debug('Bump WebSocket: Open requested', context, new Date().toLocaleString());
  if (!socket) {
    log.debug('Bump WebSocket: Open triggered', context, new Date().toLocaleString());
    try {
      socket = bumpInterface.create();
    } catch (e) {
      log.error('Bump WebSocket: Failed to Open', e);

      // TODO: If we are offline, BumpComponent will re-issue open when back online so no need to implement
      //       a timeout here. This is likely a race condition if we are here and offline (open shouldn't be called when offline)
      //
      if (!bumpInterface.isOffline()) {
        const reconnectTimeout = bumpInterface.reconnectTimeout();
        if (reconnectTimeout > 0) {
          setTimeout(() => {
            open('Automatic Retry on Failed Open');
          }, reconnectTimeout);
        }
      }
    }
    track({
      readyState: socket.readyState,
    });
  }
};

export const send = (message) => {
  log.debug('Bump WebSocket: Send ', new Date().toLocaleString(), message);
  if (socket?.readyState === WebSocket.OPEN) {
    socket.send(message);
  }
};

// This is required to handle case where socket gets closed but client doesn't receive an onclose message, perhaps
// due to a network error.
export const checkSocketConnection = () => {
  if (!bumpInterface.isOffline() && selectors.isBumpAuthenticed(store.getState())) {
    send('PING');
    pingPongTimer = setTimeout(() => {
      pingPongTimer = null;
      log.debug("Bump WebSocket: didn't receive pong, need to reconnect", new Date().toLocaleString());

      close(bumpTypes.CLOSE_BUMP_NEEDS_RECONNECTION, 'Close from Checking Socket Connection');
    }, 3000);
  }
};

// =================================================================================================
// Socket Event Handlers
// =================================================================================================

const getSocketState = (readyState) => {
  switch (readyState) {
    case 0:
      return 'CONNECTING';
    case 1:
      return 'OPEN';
    case 2:
      return 'CLOSING';
    case 3:
      return 'CLOSED';
    default:
      log.error('Unhandled/Unknown Bump Socket State', readyState);
      return 'UNKNOWN';
  }
};

export const onClose = (event) => {
  const eventSocket = event.currentTarget;
  const closingReconnectedSocket = eventSocket === socketBeingReconnected;

  const timeOfClose = new Date().toLocaleString();
  log.debug(
    `Bump WebSocket: Closed, ${timeOfClose}, closingReconnectedSocket=${closingReconnectedSocket}`,
    event?.code,
    event?.reason,
  );

  if (closingReconnectedSocket) {
    socketBeingReconnected = undefined;
  } else {
    socket = undefined;
    store.dispatch(actions.setBumpSocketState(getSocketState(eventSocket?.readyState)));

    // bad idea to auto reconnect here - we might not have auth
    // const reconnectTimeout = bumpInterface.reconnectTimeout();
    // if (reconnectTimeout > 0 && !bumpInterface.isOffline()) {
    //   setTimeout(() => {
    //     if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
    //       open('Automatic Reconnect after Close');
    //     }
    //   }, reconnectTimeout);
    // }
  }

  track({
    readyState: eventSocket?.readyState,
    type: event?.type,
    code: event?.code,
    reason: closingReconnectedSocket ? 'Closing Reconnected Socket' : event?.reason,
  });
};

export const onError = (event) => {
  const eventSocket = event.currentTarget;
  log.debug('Bump WebSocket: Error', new Date().toLocaleString(), event?.code, event?.reason);
  store.dispatch(actions.setBumpSocketState(bumpTypes.SocketStates.ERROR));

  track({
    readyState: eventSocket?.readyState,
    type: event?.type,
    code: event?.code,
    reason: event?.reason,
  });
};

export const onMessage = (event) => {
  let message = event.data;
  log.debug('Bump WebSocket: message received ', new Date().toLocaleString(), event.data);

  switch (event?.data) {
    case '_AUTHENTICATED_':
      if (socketBeingReconnected) {
        socketBeingReconnected.close();
      }
      store.dispatch(actions.setBumpSocketState('AUTHENTICATED'));
      break;
    case '_AUTHENTICATION_FAILED_':
      {
        // TODO: temporary code for debug - keep it only for one release to make sure everything is fine
        const authSession = store.getState()?.authStore; // TODO: temporary hack
        crashReporterInterface.reportError(Error('Web Socket _AUTHENTICATION_FAILED_ - force re auth'), {
          custom: {
            now: DateTime.utc().toISO(),
            datasetId: authSession?.datasetId,
            accessToken: authSession?.accessToken?.length,
            accessTokenExpired: authSession?.accessTokenExpired,
            refreshToken: authSession?.refreshToken?.length,
            refreshTokenExpired: authSession?.refreshTokenExpired,
          },
        });
        // TODO: end of temporary code

        verify(false, 'Web Socket _AUTHENTICATION_FAILED_ - force re auth');
        track({
          readyState: event.currentTarget?.readyState,
          type: event?.type,
          reason: event?.data,
        });
        // force re-auth
        // TODO: need QCS to tell us if dataset or access token is invalid
        bumpInterface.reAuth();
      }
      break;
    case '_CLOSE_': // server is requesting client to reconnect
      socketBeingReconnected = socket;
      socket = undefined;

      store.dispatch(actions.setBumpSocketState('RECONNECTING'));
      open('Server Requesting a Reconnect');
      break;
    case 'pong':
    case '_PONG_':
      if (pingPongTimer) {
        clearTimeout(pingPongTimer);
        pingPongTimer = null;
      }
      break;
    default:
      try {
        message = event.data ? JSON.parse(event.data) : undefined;
        store.dispatch(actions.receivedBumpMessage(message));
      } catch (e) {
        log.warn('Unhandled (non-json) Bump Message', e.toString?.(), event.data);
      }
  }
};

export const onOpen = (event) => {
  const eventSocket = event.currentTarget;
  log.debug('Bump WebSocket: opened', new Date().toLocaleString(), eventSocket?.readyState);

  track({
    readyState: eventSocket?.readyState,
    type: event?.type,
  });
  tracker.timeEvent(tracker.events.webSocket);

  store.dispatch(actions.setBumpSocketState(getSocketState(eventSocket?.readyState)));
};

const codes = {
  1000: 'Normal Closure',
  1001: 'Going Away',
  1002: 'Protocol Error',
  1003: 'Unsupported Data',
  1005: 'No Status Received',
  1006: 'Abnormal Closure',
  1007: 'Invalid frame payload data',
  1008: 'Policy Violation',
  1009: 'Message too big',
  1010: 'Missing Extension',
  1011: 'Internal Error',
  1012: 'Service Restart',
  1013: 'Try Again Later',
  1014: 'Bad Gateway',
  1015: 'TLS Handshake',
};

const track = (props) => {
  const readyStateString = getSocketState(props?.readyState);
  const codeString = codes[props?.code];
  tracker.track(tracker.events.webSocket, {
    ...props,
    readyStateString,
    codeString,
  });
};
